import { useEffect, useState, type ReactNode } from "react";
import styles from "./DatasetPreview.module.css";

export type DatasetPreviewMode = "card" | "table";

export type DatasetPreviewProps = {
  label: string;
  imageUrl?: string | null;
  mode?: DatasetPreviewMode;
  className?: string;
  // 섬네일 로드가 실패하면 1회 호출한다. 주된 원인은 presigned URL 만료다.
  // 부모는 dataset 을 다시 조회해 새 URL 을 받는 데 쓴다.
  // 호출 여부와 관계없이 이 프리뷰는 이니셜 표시로 바꾼다.
  onImageError?: () => void;
};

export function DatasetPreview({
  label,
  imageUrl = null,
  mode = "card",
  className,
  onImageError,
}: DatasetPreviewProps): ReactNode {
  const classNames = [styles.preview, styles[mode], className ?? ""].filter(Boolean).join(" ");
  // imageUrl 이 바뀌면(다시 조회해 새 presigned URL 을 받으면) 실패 상태를 해제하고 다시 시도한다.
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [imageUrl]);

  const handleError = () => {
    setFailed(true);
    onImageError?.();
  };

  return (
    <div className={classNames} aria-hidden="true">
      {imageUrl !== null && !failed ? (
        <img className={styles.image} src={imageUrl} alt="" loading="lazy" onError={handleError} />
      ) : (
        <span className={styles.initials}>{formatDatasetInitials(label)}</span>
      )}
    </div>
  );
}

function formatDatasetInitials(value: string): string {
  const words = value.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const initials = words.map((word) => word[0]?.toUpperCase() ?? "").join("");
  return initials === "" ? "DS" : initials;
}
